import ErrorHandler from '../utils/ErrorHandler';
import { getCurrentOrgID } from './organization';
import { supabase } from '../data/client';

const createNotification = async (details, getRecipients = false) => {
    try {
        const orgID = await getCurrentOrgID();
        let notificationsToCreate = [];

        if(!Array.isArray(details)){
            notificationsToCreate = [details];
        }else{
            notificationsToCreate = details;
        }

        const notifications = notificationsToCreate.map(notification => ({
            title: notification.title,
            parameters: notification.parameters , 
            isRead: false, 
            message: notification.message, 
            organizationID: orgID || notification.organizationID,
            priority: notification.priority || "STANDARD", 
            sentAt: new Date(), 
            targetPersonID: notification.targetPersonID,
            notificationType: notification.type,
            path: notification.path
        }))

        const {data, error} = await supabase.functions.invoke('send-notifications', {
            body: { notifications, getRecipients }
        });

        if(error){
            // Edge function 500s are already captured server-side in Sentry
            // (reach-edge-functions project). Don't double-report from the client.
            // See CHURCH-APP-15Z.
            console.warn('[createNotification] send-notifications failed:', error.message);
            return;
        }

        // Partial success: notifications were created but some email/push deliveries
        // failed. Log but don't throw — the notification rows exist. See CHURCH-APP-15S.
        if(data?.errors?.length > 0){
            console.warn('[createNotification] Partial send failure — some deliveries failed:', data.errors);
        }
    } catch(e) {
        // Swallow — fire-and-forget callers don't need to handle this, and server-side
        // Sentry coverage handles real failures. See CHURCH-APP-15Z.
        console.warn('[createNotification] error (swallowed):', e?.message);
    }
}


const updateNotifications = async (notifications) => {

    const updateToRead = notifications?.filter(n => n.isRead).map(n => n.id);
    const updateToUnRead = notifications?.filter(n => !n.isRead).map(n => n.id);

    // Batch .update() can hit Postgres deadlocks (40P01) when concurrent
    // mark-as-read calls touch overlapping rows. Transient — retry with backoff.
    const runUpdate = async (isRead, ids) => {
        let error = null;
        for (let attempt = 0; attempt < 3; attempt++) {
            const res = await supabase.from('Notifications').update({ isRead }).in('id', ids).select();
            error = res.error;
            if (!error) return null;
            if (error.code === '40P01' || error.message?.includes('deadlock')) {
                await new Promise(r => setTimeout(r, 150 * (attempt + 1)));
                continue;
            }
            break;
        }
        return error;
    };

    if(updateToRead?.length > 0){
        const error = await runUpdate(true, updateToRead);

        if(error){
            ErrorHandler(Error(error.message), {mutation: "updateNotification", input: notifications})
            throw Error();
        }
    }

    if(updateToUnRead?.length > 0){
        const error = await runUpdate(false, updateToUnRead);

        if(error){
            ErrorHandler(Error(error.message), {mutation: "updateNotification", input: notifications})
            throw Error();
        }
    }

    //OS app badge is owned by the NotificationBadgeSync effect in src/app/_layout.jsx —
    //it derives the count from the loggedInUser cache. Don't write it here.
    return {updateToRead, updateToUnRead}
}


const deleteNotifications = async ({ids}) => {
    const {error} = await supabase.from('Notifications').delete().in('id', ids);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteNotifications", ids})
        throw Error();
    }

    return ids
}


export { 
    createNotification,
    updateNotifications,
    deleteNotifications,
}